import * as React from 'react';

import {
  CategorySortOption,
  enumCategorySortOption,
} from '@rematter/frontend-lib-api-client';
import { useSettingsLocation } from '@rematter/modules/settings/_state/settingsModule.state';
import {
  Button,
  DataGrid,
  ListScreenLayout,
  Pagination,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import { CategoryRecord } from '../../../_graphql/queryCategoriesList';
import * as state from '../../../_state/inventory.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { CategoriesFilters } from './CategoriesFilters';
import { CategoryCreateDialog } from './CategoryCreateDialog';
import { useCategoriesColumns } from './hooks/useCategoriesColumns';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: CategorySortOption; desc: CategorySortOption }
> = {
  name: {
    asc: enumCategorySortOption.NameAsc,
    desc: enumCategorySortOption.NameDesc,
  },
  createdAt: {
    asc: enumCategorySortOption.CreatedAtAsc,
    desc: enumCategorySortOption.CreatedAtDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ CATEGORIES LIST VIEW ⚛️ ⚛️ ⚛️
//
export const CategoriesListView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteCategories,
  );
  const [selectedKeys, setSelectedKeys] = useAtom(
    state.$categoriesSelectedKeys,
  );
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCategoryCreateDialogOpen,
  );
  const setCurrentSorting = useSetAtom(state.$categoriesSorting);

  const handleRowClick = React.useCallback(
    (category: CategoryRecord) => {
      setLocation(`/inventory/categories/${category.id}`);
    },
    [setLocation],
  );
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumCategorySortOption.NameAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleCreateCategory = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  const { columns, columnVisibilityState } = useCategoriesColumns({
    onRowClick: handleRowClick,
  });

  const categories = data?.categories ?? [];
  const total = data?.totalCount ?? 0;
  const emptyPanelProps = {
    title: t.inventoryView.categoriesPanel.emptyState.title,
    description: (
      <span>{t.inventoryView.categoriesPanel.emptyState.description}</span>
    ),
    action: (
      <Button
        onPress={() => setIsCreateDialogOpen(true)}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.inventoryView.categoriesPanel.actions.create}
      </Button>
    ),
    centered: true,
  };

  return (
    <>
      <ListScreenLayout
        className="@CategoriesListView"
        filters={<CategoriesFilters />}
        actions={
          <Button
            onPress={handleCreateCategory}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.inventoryView.categoriesPanel.actions.create}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<CategoryRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={categories}
          rowKey="id"
          aria-label={t.inventoryView.categoriesPanel.ariaLabel}
          selectionMode="multiple"
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          columnVisibilityState={columnVisibilityState}
          className="w-full text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description:
                    t.inventoryView.categoriesPanel.errors.loadingFailed(
                      error.message,
                    ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <CategoryCreateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => refetch?.()}
      />
    </>
  );
};
